import { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Lightbulb } from "lucide-react";

export default function ExplainTab({ id }) {
  const [explain, setExplain] = useState(null);
  useEffect(() => {
    const load = () => endpoints.explain(id).then((r) => setExplain(r.data.explain));
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [id]);

  if (!explain) return <div className="text-sm text-muted-foreground">SHAP explanations run at the end of the pipeline…</div>;

  if (!explain.available) {
    return (
      <div className="border border-border rounded-sm p-6 bg-card">
        <div className="label-mono mb-2 flex items-center gap-2">
          <Lightbulb className="w-3.5 h-3.5 text-accent" /> // SHAP UNAVAILABLE
        </div>
        <p className="text-sm text-muted-foreground">{explain.reason || "SHAP could not be computed for this model."}</p>
        <p className="text-xs text-muted-foreground mt-2">See the Evaluation tab for feature importance derived from the model directly.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="border border-border rounded-sm p-5 bg-card">
        <div className="label-mono mb-2 flex items-center gap-2">
          <Lightbulb className="w-3.5 h-3.5 text-accent" /> // GLOBAL SHAP
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Mean absolute SHAP value per feature over <span className="num text-foreground">{explain.n_samples}</span> holdout samples.
          Higher = larger average impact on model output.
        </p>
      </div>

      <div>
        <div className="label-mono mb-3">// FEATURE ATTRIBUTIONS</div>
        <div className="border border-border rounded-sm p-4 bg-card h-[520px]">
          <ResponsiveContainer>
            <BarChart data={explain.global.slice().reverse()} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" stroke="hsl(0 0% 45%)" fontSize={10} />
              <YAxis dataKey="feature" type="category" stroke="hsl(0 0% 45%)" fontSize={10} width={200} />
              <Tooltip contentStyle={{ background: "hsl(0 0% 6%)", border: "1px solid hsl(0 0% 14%)" }} formatter={(v) => v.toFixed(4)} />
              <Bar dataKey="mean_abs_shap" fill="hsl(45 93% 47%)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {explain.local?.length > 0 && (
        <div>
          <div className="label-mono mb-3">// LOCAL EXPLANATIONS (SAMPLE ROWS)</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {explain.local.map((row) => (
              <div key={row.row} className="border border-border rounded-sm p-4 bg-card" data-testid={`local-row-${row.row}`}>
                <div className="label-mono mb-3">ROW #{row.row}</div>
                <div className="space-y-1.5">
                  {row.contributions.map((c) => (
                    <div key={c.feature} className="flex items-center gap-2 text-xs">
                      <span className="font-mono text-muted-foreground truncate flex-1">{c.feature}</span>
                      <span className={`num font-medium ${c.value >= 0 ? "text-accent" : "text-blue-400"}`}>
                        {c.value >= 0 ? "+" : ""}{c.value.toFixed(3)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
